<template>
  <spinner v-if="loading" />
  <div v-else class="screen-container">
    <div class="back-button" @click="goBackToTickets">
      <v-icon class="back-icon">arrow_back</v-icon>
      <span class="back-text">{{ adminText('workflowsAll.back_to_tickets_de31c24', 'Back to tickets') }}</span>
    </div>
    <v-layout column>
      <v-flex class="title-screen" xs12>
        <span>{{ adminText('workflowsAll.gratis_tickets_a63f4c3', 'Gratis tickets') }}</span>
      </v-flex>
      <v-flex>
        <p class="description">{{ adminText('workflowsAll.add_and_manage_your_gratis_tickets_1b25d12', 'Add and manage your gratis tickets.') }}</p>
      </v-flex>
      <v-flex class="mb-16">
        <main-button depressed @click="onCreateGratisTicketClick">
          <span class="add-symbol">+</span>{{ adminText('workflowsAll.new_gratis_ticket_4314039', 'New gratis ticket') }}
        </main-button>
      </v-flex>
      <div class="gratis-table">
        <div
          v-if="gratisTickets.length === 0"
          class="no-tickets-container"
        >
          <span class="no-tickets-message">{{ adminText('workflowsAll.no_matching_records_found_0c7f0a6', 'No matching records found.') }}</span>
        </div>
        <v-data-table
          v-else
          :headers="workflowUi(headers)"
          :items="workflowUi(gratisTickets)"
          :pagination.sync="tablePagination"
          :rows-per-page-items="[5, 10, 25, 100]"
          class="gratis-data-table"
        >
          <template v-slot:items="{ item }">
            <tr
              class="gratis-row"
              @mouseenter="hoveredRow = item.id"
              @mouseleave="hoveredRow = null"
              @click="openUpdateDialog(item)"
            >
              <td>{{ workflowLabel(item.name) }}</td>
              <td>
                <div class="category-cell">
                  <v-icon :color="item.categoryColor" size="13">fiber_manual_record</v-icon>
                  <span>{{ workflowLabel(item.categoryName) }}</span>
                </div>
              </td>
              <td :title="adminText('workflowsAll.gratis_active_issued', 'Active / issued')">{{ workflowLabel(item.quantityLabel) }}</td>
              <td>{{ workflowLabel(item.priceLabel) }}</td>
              <td class="text-xs-right action-cell">
                <v-btn
                  icon
                  flat
                  small
                  class="delete-btn"
                  :class="{ 'delete-btn--visible': hoveredRow === item.id }"
                  :disabled="cancelling"
                  :title="adminText('workflowsAll.gratis_cancel_unscanned', 'Cancel unscanned tickets')"
                  :aria-label="adminText('workflowsAll.gratis_cancel_unscanned', 'Cancel unscanned tickets')"
                  @click.stop="openDeleteDialog(item)"
                >
                  <v-icon color="#DB384C" small>delete</v-icon>
                </v-btn>
              </td>
            </tr>
          </template>
        </v-data-table>
      </div>
    </v-layout>
    <delete-dialog
      :content="adminText('workflowsAll.gratis_cancel_confirmation', 'Are you sure you want to cancel the unscanned tickets from &quot;{{name}}&quot;? Scanned tickets remain valid. An ordinary table is released only when no valid tickets remain. This operation cannot be undone.', { name: ticketToDelete ? ticketToDelete.name : '' })"
      :on-delete="deleteTicket"
      :show.sync="showDeleteDialog"
      :title="adminText('workflowsAll.gratis_cancel_title', 'Cancel gratis tickets')"
      :action-message="adminText('workflowsAll.gratis_cancel_action', 'Cancel tickets')"
      wait-for-completion
    />
    <simple-snackbar :open.sync="snackbarOpen" :message-prop="snackbarMessage" :color="snackbarColor" />
    <update-gratis-ticket-modal
      v-if="showUpdateDialog"
      :show.sync="showUpdateDialog"
      :ticket="ticketToUpdate"
      :event-id="eventId"
      @updated="onTicketUpdated"
    />
    <demo-modal v-if="demoModal" :user="user" @close="demoModal = false" />
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import { GET_ORDER_ITEMS, CANCEL_GRATIS_ORDER_ITEM } from '@/graphql/orderItem.graphql'
import DeleteDialog from '../shared/dialogs/delete-dialog.vue'
import SimpleSnackbar from '../shared/snackbars/simple-snackbar.vue'
import DemoModal from '@/components/organizations/DemoModal.vue'
import UpdateGratisTicketModal from './UpdateGratisTicketModal.vue'

const GRATIS_ORDER_ITEM_COLOR = '#ADD8E6'

export default {
  mixins: [WorkflowDisplay],
  name: 'gratis-tickets-list',
  components: {
    DeleteDialog,
    SimpleSnackbar,
    DemoModal,
    UpdateGratisTicketModal,
  },
  props: {
    eventId: String,
    user: Object,
    organizationSlug: String,
  },
  data() {
    return {
      loading: true,
      allTickets: [],
      headers: [
        { text: 'workflowsAll.ticket_name_49f0db5', value: 'name', sortable: false },
        { text: 'workflowsAll.category_a3c686e', value: 'categoryName', sortable: false },
        { text: 'workflowsAll.quantity_44f6af6', value: 'quantity', sortable: false },
        { text: 'workflowsAll.price_3e8248e', value: 'price', sortable: false },
        { text: '', value: 'actions', sortable: false, width: '48px' },
      ],
      tablePagination: {
        rowsPerPage: 10,
        page: 1,
      },
      hoveredRow: null,
      showDeleteDialog: false,
      ticketToDelete: null,
      showUpdateDialog: false,
      ticketToUpdate: null,
      demoModal: false,
      cancelling: false,
      snackbarOpen: false,
      snackbarMessage: '',
      snackbarColor: 'green',
    }
  },
  apollo: {
    getOrderItems: {
      query: GET_ORDER_ITEMS,
      variables() {
        return {
          eventId: this.eventId,
          pagination: { page: 1, per_page: -1 },
          isGratis: true,
        }
      },
      manual: true,
      result({ data, loading }) {
        if (!loading) {
          const items = data.getOrderItems.items || []
          this.allTickets = items.map(t => this.mapTicket(t))
          this.loading = false
        }
      },
    },
  },
  computed: {
    gratisTickets() {
      return this.allTickets
    },
  },
  methods: {
    mapTicket(ticket) {
      const category = ticket.organizationItemCategory

      let type = 'Seat'
      if (ticket.itemMeta && ticket.itemMeta.table) type = 'Table'
      else if (ticket.itemMeta && ticket.itemMeta.parter) type = 'Parter'

      const fromDate = new Date(ticket.from)
      const toDate = new Date(ticket.to)
      const fmt = (date) => {
        const y = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(date)
        const m = new Intl.DateTimeFormat('en', { month: 'numeric' }).format(date)
        const d = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(date)

        return `${d}.${m}.${y}`
      }

      return {
        ...ticket,
        type,
        categoryColor: category ? category.color : GRATIS_ORDER_ITEM_COLOR,
        categoryName: category ? category.name : 'workflowsAll.free_4455fad',
        quantityLabel: `${Math.max(0, ticket.initialQuantity - ((ticket.itemMeta || {}).cancelledGratisQuantity || 0))}/${ticket.initialQuantity}`,
        priceLabel: `${ticket.price} ${ticket.currency || 'EUR'}`,
        from: fmt(fromDate),
        to: fmt(toDate),
      }
    },
    formatPrice(price, currency) {
      const num = parseFloat(price || '0')
      const formatted = num.toFixed(2).replace('.', ',')
      const symbol = currency === 'EUR' ? '€' : currency || '€'

      return `${formatted} ${symbol}`
    },
    goBackToTickets() {
      this.goTo(
        this.isRegularOrganization()
          ? this.routes.organizer.tickets
          : this.routes.sportOrganizer.ticketsSport,
        {
          eventId: this.eventId,
          organizationSlug: this.organizationSlug,
        },
      )
    },
    onCreateGratisTicketClick() {
      if (!this.user.isVerifiedByCoreEvent) {
        this.demoModal = true
      } else {
        this.goTo(
          this.isRegularOrganization()
            ? this.routes.organizer.createGratisTicket
            : this.routes.sportOrganizer.createGratisTicketSport,
          {
            eventId: this.eventId,
            user: this.user,
            organizationSlug: this.organizationSlug,
          },
        )
      }
    },
    isRegularOrganization() {
      return this.$route.path.includes('/organizations/')
    },
    openDeleteDialog(ticket) {
      this.ticketToDelete = ticket
      this.showDeleteDialog = true
    },
    openUpdateDialog(ticket) {
      this.ticketToUpdate = ticket
      this.showUpdateDialog = true
    },
    async onTicketUpdated() {
      await this.$apollo.queries.getOrderItems.refetch()
    },
    async deleteTicket() {
      if (!this.ticketToDelete || this.cancelling) return false
      this.cancelling = true
      try {
        const { data } = await this.$apollo.mutate({
          mutation: CANCEL_GRATIS_ORDER_ITEM,
          variables: { eventId: this.eventId, orderItemId: this.ticketToDelete.id },
        })
        const result = data.cancelGratisOrderItem
        this.showDeleteDialog = false
        this.ticketToDelete = null
        this.snackbarColor = 'green'
        this.snackbarMessage = result.cancelledCount
          ? this.adminText('workflowsAll.gratis_cancelled_result', '{{cancelled}} ticket(s) cancelled. {{remaining}} scanned ticket(s) remain valid. Capacity was returned where possible under the table rules.', { cancelled: result.cancelledCount, remaining: result.remainingCount })
          : this.adminText('workflowsAll.gratis_nothing_to_cancel', 'No unscanned tickets remain to cancel. Scanned tickets stay valid.')
        this.snackbarOpen = true
        await this.$apollo.queries.getOrderItems.refetch()
      } catch (e) {
        this.snackbarColor = 'red'
        this.snackbarMessage = e.message || this.adminText('workflowsAll.gratis_cancel_error', 'Unable to cancel gratis tickets. Please try again.')
        this.snackbarOpen = true

        return false
      } finally { this.cancelling = false }
    },
  },
}
</script>

<style scoped>
.screen-container {
  position: relative;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 12px 0;
  margin-bottom: 1rem;
  width: fit-content;
}

.back-icon {
  color: #0277bd !important;
  font-size: 24px !important;
}

.back-text {
  font-family: 'Raleway';
  font-weight: 700;
  font-size: 18px;
  line-height: 1.2;
  letter-spacing: -0.27px;
  text-transform: uppercase;
  color: #0277bd;
}

.title-screen {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
  margin-bottom: 0.5rem;
}

.description {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 16px;
  line-height: 1.5;
  color: #979797;
  margin-bottom: 1.5rem;
}

.add-symbol {
  font-size: 1.875rem;
  margin-right: 0.8125rem;
  font-weight: 200;
}

.gratis-table {
  margin-top: 1rem;
}

.no-tickets-container {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #f5f5f5;
  border-radius: 8px;
}

.no-tickets-message {
  color: #979797;
  padding: 20px;
}

.gratis-data-table {
  border: 2px solid #f5f5f5;
  border-radius: 8px;
  overflow: hidden;
}

.gratis-data-table >>> .v-table {
  border-collapse: collapse;
  border-spacing: 0;
}

.gratis-data-table >>> thead th {
  font-family: 'Raleway';
  font-weight: 700;
  font-size: 16px !important;
  letter-spacing: -0.24px;
  color: #666 !important;
  background-color: #f5f5f5;
  height: 48px;
  border-bottom: 2px solid #f5f5f5 !important;
}

.gratis-data-table >>> tbody td {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 16px !important;
  color: #666;
  height: 48px;
  border-bottom: 1px solid #f5f5f5 !important;
}

.gratis-data-table >>> tbody tr:hover {
  background-color: #f0f6fa !important;
}

.gratis-row {
  cursor: pointer;
}

.gratis-data-table >>> .v-datatable__actions {
  border-top: 1px solid #f5f5f5;
}

.category-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.action-cell {
  width: 48px;
}

.delete-btn {
  opacity: 0;
  transition: opacity 0.15s ease;
  margin: 0;
}

.delete-btn--visible {
  opacity: 1;
}

@media screen and (max-width: 800px) {
  .title-screen {
    display: none;
  }

  .screen-container {
    margin-top: 1.5rem;
  }

  .delete-btn {
    opacity: 1;
  }
}
</style>
